/**
 * src/components/spatial/SpatialObjectsControls.tsx
 *
 * Floating control cluster for the spatial-objects subsystem. Surfaces
 * every toggle the spec calls out:
 *
 *   - provider indicator                  ("Mapbox", "MapTiler SDK", …)
 *   - spatial object toggle               (master on/off)
 *   - floor selector                      (canonical floorNumber)
 *   - slice mode                          (toggle)
 *   - explode floors mode                 (toggle + gap slider)
 *   - show policy-linked objects only     (executionRuleId is non-null)
 *   - show contract-linked objects only   (contractAddress is non-null)
 *
 * Adds a "MapTiler complement" panel that lights up when
 * `REACT_APP_MAPTILER_KEY` is configured. From there the user can
 * toggle the MapTiler raster overlay on/off, switch styles (satellite,
 * topo, …) and tweak opacity. The overlay never replaces Mapbox —
 * it's purely additive, so all existing Mapbox features keep working.
 */

import React from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useSpatialObjects } from './SpatialObjectsContext';
import { debugSpatialEnvSnapshot } from '../../services/spatial/spatialEnv';

/** Common MapTiler raster styles a user might pick at runtime. */
const MAPTILER_STYLE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'satellite-v2', label: 'Satellite' },
  { id: 'hybrid',       label: 'Hybrid' },
  { id: 'topo-v2',      label: 'Topo' },
  { id: 'outdoor-v2',   label: 'Outdoor' },
  { id: 'streets-v2',   label: 'Streets' },
  { id: 'winter-v2',    label: 'Winter' },
];

export interface SpatialObjectsControlsProps {
  provider: SpatialMapProvider | null;
  /** Floor numbers the user can pick from. Defaults to 1..5. */
  availableFloorNumbers?: number[];
  /** Optional className/style hook so the host can position the card. */
  style?: React.CSSProperties;
}

const cardStyle: React.CSSProperties = {
  position:      'absolute',
  zIndex:        1900,
  background:    'rgba(15, 23, 42, 0.92)',
  color:         '#e2e8f0',
  borderRadius:  12,
  padding:       '10px 12px',
  boxShadow:     '0 6px 24px rgba(0, 0, 0, 0.45)',
  border:        '1px solid rgba(99, 102, 241, 0.35)',
  font:          '500 12px/1.4 system-ui, -apple-system, sans-serif',
  pointerEvents: 'auto',
  minWidth:      240,
  backdropFilter:'blur(6px)',
};

const sectionLabel: React.CSSProperties = {
  fontSize:       10.5,
  color:          '#94a3b8',
  fontWeight:     700,
  textTransform:  'uppercase',
  letterSpacing:  0.4,
  marginBottom:   4,
  marginTop:      8,
};

const toggleBtnBase: React.CSSProperties = {
  border:        '1px solid rgba(148, 163, 184, 0.3)',
  background:    'rgba(30, 41, 59, 0.85)',
  color:         '#e2e8f0',
  borderRadius:  6,
  padding:       '4px 8px',
  font:          '600 11px system-ui, -apple-system, sans-serif',
  cursor:        'pointer',
  whiteSpace:    'nowrap',
};

function pillStyle(active: boolean, color = 'rgba(99, 102, 241, 0.45)', disabled = false): React.CSSProperties {
  return {
    ...toggleBtnBase,
    background: active ? color : 'rgba(30, 41, 59, 0.55)',
    borderColor: active ? color : 'rgba(148, 163, 184, 0.3)',
    color: active ? '#fff' : '#e2e8f0',
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const SpatialObjectsControls: React.FC<SpatialObjectsControlsProps> = ({
  provider,
  availableFloorNumbers = [1, 2, 3, 4, 5],
  style,
}) => {
  const {
    view,
    filters,
    mapTiler,
    setEnabled,
    setSelectedFloorNumber,
    setSliceMode,
    setExplodeFloors,
    setExplodeGap,
    setPolicyLinkedOnly,
    setContractLinkedOnly,
    setEventsOnly,
    setMapTilerOverlayStyle,
    setMapTilerOverlayOpacity,
  } = useSpatialObjects();

  return (
    <div style={{ ...cardStyle, ...style }}>
      {/* Header — provider indicator + master toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden style={{ fontSize: 14 }}>🏢</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#a5b4fc' }}>Spatial objects</div>
          <div
            title={provider ? `Map provider: ${provider.displayName}` : 'No map provider attached.'}
            style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <span
              aria-hidden
              style={{
                display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                background: provider ? '#34d399' : '#94a3b8',
                boxShadow:  provider ? '0 0 6px #34d399' : 'none',
              }}
            />
            <span>Provider: {provider?.displayName ?? 'none'}</span>
            {mapTiler.available && (
              <span
                title={mapTiler.enabled
                  ? `MapTiler "${mapTiler.style}" overlay is on top of the basemap.`
                  : 'MapTiler key configured — toggle below to add a raster overlay on top of Mapbox.'}
                style={{
                  marginLeft: 4,
                  padding: '0 5px',
                  borderRadius: 999,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color:  mapTiler.enabled ? '#fde68a' : '#cbd5e1',
                  border: `1px solid ${mapTiler.enabled ? 'rgba(245, 158, 11, 0.55)' : 'rgba(148, 163, 184, 0.35)'}`,
                  background: mapTiler.enabled ? 'rgba(245, 158, 11, 0.18)' : 'rgba(30, 41, 59, 0.45)',
                }}
              >
                + MapTiler
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEnabled(!view.enabled)}
          aria-pressed={view.enabled}
          title={view.enabled ? 'Hide all spatial objects.' : 'Show GeoLink spatial objects on the map.'}
          style={pillStyle(view.enabled, 'rgba(34, 197, 94, 0.45)')}
        >
          {view.enabled ? 'On' : 'Off'}
        </button>
      </div>

      {/* Floor selector */}
      <div style={sectionLabel}>Floor</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <button
          type="button"
          onClick={() => setSelectedFloorNumber(null)}
          style={pillStyle(view.selectedFloorNumber == null)}
          title="Show every floor."
        >
          All
        </button>
        {availableFloorNumbers.map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setSelectedFloorNumber(view.selectedFloorNumber === n ? null : n)}
            style={pillStyle(view.selectedFloorNumber === n)}
            title={`Show only floor ${n} (canonical floorNumber).`}
          >
            {n}
          </button>
        ))}
      </div>

      {/* Slice / explode */}
      <div style={sectionLabel}>View modes</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <button
          type="button"
          onClick={() => setSliceMode(!view.sliceMode)}
          style={pillStyle(view.sliceMode, 'rgba(239, 68, 68, 0.45)')}
          title="Toggle a horizontal cross-section. Selecting a floor cancels slice mode."
        >
          ✂ Slice
        </button>
        <button
          type="button"
          onClick={() => setExplodeFloors(!view.explodeFloors)}
          style={pillStyle(view.explodeFloors, 'rgba(168, 85, 247, 0.45)')}
          title="Spread floors apart vertically so each floor's interior is visible."
        >
          ⤴ Explode
        </button>
      </div>
      {view.explodeFloors && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10.5, color: '#94a3b8' }}>Gap</span>
          <input
            type="range"
            min={2}
            max={20}
            step={1}
            value={view.explodeGapMeters}
            onChange={e => setExplodeGap(Number(e.target.value))}
            aria-label="Explode gap in metres"
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: 10.5, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
            {view.explodeGapMeters}m
          </span>
        </div>
      )}

      {/* Linkage filters */}
      <div style={sectionLabel}>Show only</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <button
          type="button"
          onClick={() => setPolicyLinkedOnly(!filters.policyLinkedOnly)}
          style={pillStyle(filters.policyLinkedOnly)}
          title="Hide objects whose executionRuleId is null (i.e. not linked to a GeoLink policy/rule)."
        >
          🛡 Policy-linked
        </button>
        <button
          type="button"
          onClick={() => setContractLinkedOnly(!filters.contractLinkedOnly)}
          style={pillStyle(filters.contractLinkedOnly)}
          title="Hide objects whose contractAddress is null."
        >
          ⛓ Contract-linked
        </button>
        <button
          type="button"
          onClick={() => setEventsOnly(!filters.eventsOnly)}
          style={pillStyle(filters.eventsOnly)}
          title="Show only objects hosting an event. Anything happening right now is painted amber."
        >
          🎉 Events
        </button>
      </div>

      {/* MapTiler complement — always visible so devs can see whether
          the key was picked up. When `available` is false (no key
          inlined into the bundle) we show a clearly-disabled panel
          with a "restart dev server" hint, instead of hiding the
          section silently. */}
      <div style={sectionLabel}>MapTiler complement</div>
      {!mapTiler.available && (
        <div style={{
          padding: '6px 10px',
          marginBottom: 6,
          borderRadius: 8,
          background: 'rgba(245, 158, 11, 0.10)',
          border:     '1px solid rgba(245, 158, 11, 0.35)',
          color:      '#fde68a',
          fontSize:   10.5,
          lineHeight: 1.4,
        }}>
          <div>⚠ <strong>REACT_APP_MAPTILER_KEY not detected.</strong></div>
          <div style={{ marginTop: 4 }}>
            Fix in this order:
            <ol style={{ margin: '4px 0 0 16px', padding: 0 }}>
              <li>Confirm <code style={{ color: '#fef3c7' }}>.env.local</code> has{' '}
                <code style={{ color: '#fef3c7' }}>REACT_APP_MAPTILER_KEY=…</code>.</li>
              <li>Stop <code style={{ color: '#fef3c7' }}>npm start</code> (Ctrl+C).</li>
              <li>Clear the cache:{' '}
                <code style={{ color: '#fef3c7' }}>rmdir /s /q node_modules\.cache</code>{' '}
                (PowerShell:{' '}
                <code style={{ color: '#fef3c7' }}>Remove-Item -Recurse -Force node_modules\.cache</code>).</li>
              <li>Run <code style={{ color: '#fef3c7' }}>npm start</code> again, then hard-refresh the browser (Ctrl+Shift+R).</li>
            </ol>
          </div>
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer', color: '#fef3c7' }}>Show inlined env snapshot</summary>
            <pre style={{
              margin: '4px 0 0 0',
              padding: 6,
              background: 'rgba(15, 23, 42, 0.7)',
              borderRadius: 6,
              fontSize: 10,
              color: '#cbd5e1',
              maxHeight: 180,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
{JSON.stringify(debugSpatialEnvSnapshot(), null, 2)}
            </pre>
            <div style={{ marginTop: 4, color: '#94a3b8' }}>
              Or run <code style={{ color: '#fef3c7' }}>window.__geotrust_spatialEnv()</code> in DevTools.
            </div>
          </details>
        </div>
      )}
      {/* Style picker + opacity slider — visible whenever the MapTiler
          key is available. We no longer render an explicit "Overlay
          on/off" toggle: the overlay is governed by env config
          (`REACT_APP_MAPTILER_OVERLAY_ENABLED`) and the opacity slider
          can be dragged to 0% to effectively hide the layer without an
          extra control. */}
      {mapTiler.available && mapTiler.enabled && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {MAPTILER_STYLE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMapTilerOverlayStyle(opt.id)}
                style={pillStyle(mapTiler.style === opt.id, 'rgba(245, 158, 11, 0.45)')}
                title={`Switch the MapTiler overlay to "${opt.id}".`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10.5, color: '#94a3b8' }}>Opacity</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={mapTiler.opacity}
              onChange={e => setMapTilerOverlayOpacity(Number(e.target.value))}
              aria-label="MapTiler overlay opacity"
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: 10.5, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
              {(mapTiler.opacity * 100).toFixed(0)}%
            </span>
          </div>
        </>
      )}

    </div>
  );
};

export default SpatialObjectsControls;
